import { IndicatorType } from '@melodic-ui/types';

/**
 * Semantic variant name for a lesson type.
 * Drives CSS class names (timeline slivers) and IndicatorType (tags, chips).
 */
export type LessonTypeVariant = 'instructional' | 'mastery' | 'tutoring' | 'chirp';

export interface LessonTypeConfig {
	variant: LessonTypeVariant;
	/** Maps to md-content-box / md-tag IndicatorType for consistent coloring. */
	indicatorType: IndicatorType;
}

/**
 * Single source of truth for lesson type → visual identity.
 *
 * - instructional (typeID 2): brand green    — primary instructional content
 * - mastery       (typeID 1): warning orange — mastery/assessment content
 * - tutoring      (typeID 3): tertiary gray  — tutoring / test prep / conversational
 * - chirp         (typeID 4): tertiary gray  — chirp room
 */
export const LESSON_TYPE_CONFIG: Record<LessonTypeVariant, LessonTypeConfig> = {
	instructional: { variant: 'instructional', indicatorType: 'brand' },
	mastery: { variant: 'mastery', indicatorType: 'warning' },
	tutoring: { variant: 'tutoring', indicatorType: 'tertiary' },
	chirp: { variant: 'chirp', indicatorType: 'tertiary' }
};

/** Derives the semantic variant from a raw lessonTypeID. */
export function getLessonTypeVariant(lessonTypeID: number): LessonTypeVariant {
	if (lessonTypeID === 2) return 'instructional';
	if (lessonTypeID === 1) return 'mastery';
	if (lessonTypeID === 4) return 'chirp';
	return 'tutoring';
}

/** Convenience: get the full config for a raw lessonTypeID. */
export function getLessonTypeConfig(lessonTypeID: number): LessonTypeConfig {
	return LESSON_TYPE_CONFIG[getLessonTypeVariant(lessonTypeID)];
}

/**
 * Icon for a lesson type, used on the leading edge of lesson-type tags and chips.
 * Kept keyed off the raw lessonTypeID (rather than the variant) so an unrecognized
 * ID falls back to a generic book icon instead of inheriting the tutoring icon.
 */
export function getLessonTypeIcon(lessonTypeID: number): string {
	switch (lessonTypeID) {
		case 1: // Mastery
			return 'untitled-ui/general/target-05';
		case 2: // Instructional
			return 'untitled-ui/media/monitor-01';
		case 3: // Tutoring
			return 'untitled-ui/communication/message-circle-01';
		case 4: // Chirp Room
			return 'mylb/bird-outline';
		default:
			return 'untitled-ui/education/book-open-01';
	}
}
